import type { RetailerRegionView } from '@vizit/api';
import { RETAILER_CONFIG_MAP, type RetailerKey } from '@vizit/constants';
import { RETAILER_ICONS } from '@vizit/molecules';
import { useMemo } from 'react';
import type { ClearableSelectItem } from '../clearable-select';
import { ClearableSelect } from '../clearable-select';

interface RetailerSelectProps {
  /**
   * List of retailers to display, fetched from the API.
   * Returns null when fewer than 2 retailers — no point filtering.
   */
  retailers: RetailerRegionView[];
  /**
   * The currently selected retailer key, or undefined when "All retailers" is selected.
   */
  selectedRetailer?: RetailerKey;
  /**
   * Callback fired when selection changes. Receives undefined when "All" is chosen.
   */
  onRetailerChange: (retailer: RetailerKey | undefined) => void;
  /** Optional additional class name */
  className?: string;
}

function RetailerSelect(props: RetailerSelectProps) {
  const { retailers, selectedRetailer, onRetailerChange, className, ...other } = props;

  const items = useMemo<ClearableSelectItem[]>(
    () =>
      retailers.map(({ id, display_name }) => {
        const config = RETAILER_CONFIG_MAP[id as RetailerKey];
        const IconComponent = config?.baseIcon ? RETAILER_ICONS[config.baseIcon] : null;
        return {
          id,
          label: display_name,
          icon: IconComponent ? <IconComponent size={14} /> : undefined,
        };
      }),
    [retailers],
  );

  if (retailers.length <= 1) return null;

  return (
    <ClearableSelect
      ariaLabel="Retailer"
      placeholder="All retailers"
      items={items}
      selectedKey={selectedRetailer ?? null}
      onSelectionChange={(key) => onRetailerChange(key ? (key as RetailerKey) : undefined)}
      className={className}
      {...other}
    />
  );
}

export { RetailerSelect };
export type { RetailerSelectProps };
